/**
 * Every route page's chunk, by name, and the address patterns that land on it
 * (#209). App.tsx builds its pages from these loaders; the nav and the command
 * palette prefetch through `prefetchRoute(path)` on hover or focus of a link, so
 * a page's code is usually in hand by the time the click lands; and main.tsx
 * preloads the page in the address bar at boot (`preloadBootRoute`).
 *
 * One loader per MODULE, not per page: a module that exports several pages
 * (Settings, a signal source and its redirect) is one chunk, fetched once.
 */
export const routeChunks = {
  login: () => import('@/routes/LoginPage'),
  signup: () => import('@/routes/SignUpPage'),
  verifyEmail: () => import('@/routes/VerifyEmailPage'),
  mcpAuthorize: () => import('@/routes/McpAuthorizePage'),
  garminCallback: () => import('@/routes/GarminCallbackPage'),
  dashboard: () => import('@/routes/DashboardPage'),
  healthSleep: () => import('@/routes/HealthSleepPage'),
  healthActivity: () => import('@/routes/HealthActivityPage'),
  healthHeart: () => import('@/routes/HealthHeartPage'),
  healthBody: () => import('@/routes/HealthBodyPage'),
  audiences: () => import('@/routes/AudiencesOverviewPage'),
  audienceNew: () => import('@/routes/AudienceNewPage'),
  audienceDetail: () => import('@/routes/AudienceDetailPage'),
  reels: () => import('@/routes/ReelsOverviewPage'),
  reelNew: () => import('@/routes/ReelNewPage'),
  reelDetail: () => import('@/routes/ReelDetailPage'),
  social: () => import('@/routes/SocialPostsPage'),
  socialNew: () => import('@/routes/SocialPostNewPage'),
  socialDetail: () => import('@/routes/SocialPostDetailPage'),
  signals: () => import('@/routes/SignalsPage'),
  signalNew: () => import('@/routes/SignalNewPage'),
  signalSource: () => import('@/routes/SignalSourcePage'),
  signalItem: () => import('@/routes/SignalItemPage'),
  pages: () => import('@/routes/PagesOverviewPage'),
  pageNew: () => import('@/routes/PageNewPage'),
  pageView: () => import('@/routes/PageViewPage'),
  pageEdit: () => import('@/routes/PageEditPage'),
  settings: () => import('@/routes/SettingsPage'),
  apiKeys: () => import('@/routes/ApiKeysPage'),
} as const

export type RouteChunk = keyof typeof routeChunks

/**
 * Address → chunk, first match wins, so the fixed addresses come before the
 * `:param` ones they would otherwise be swallowed by (`/reels/new` before
 * `/reels/:id`). `:x` matches one segment. Redirects are not here: they carry
 * no page of their own.
 */
const PATTERNS: [pattern: string, chunk: RouteChunk][] = [
  ['/', 'dashboard'],
  ['/login', 'login'],
  ['/signup', 'signup'],
  ['/verify-email', 'verifyEmail'],
  ['/mcp/authorize', 'mcpAuthorize'],
  ['/garmin', 'garminCallback'],
  ['/health/sleep', 'healthSleep'],
  ['/health/activity', 'healthActivity'],
  ['/health/heart', 'healthHeart'],
  ['/health/body', 'healthBody'],
  ['/audiences', 'audiences'],
  ['/audiences/new', 'audienceNew'],
  ['/audiences/:id', 'audienceDetail'],
  ['/reels', 'reels'],
  ['/reels/new', 'reelNew'],
  ['/reels/:id', 'reelDetail'],
  ['/social', 'social'],
  ['/social/new', 'socialNew'],
  ['/social/:id', 'socialDetail'],
  ['/signals', 'signals'],
  ['/signals/new', 'signalNew'],
  ['/signals/sources/:sourceId', 'signalSource'],
  ['/signals/sources/:sourceId/items/:itemId', 'signalItem'],
  ['/signals/:id', 'signalSource'],
  ['/signals/:id/items/:itemId', 'signalItem'],
  ['/pages', 'pages'],
  ['/pages/new', 'pageNew'],
  ['/pages/:slug', 'pageView'],
  ['/pages/:slug/edit', 'pageEdit'],
  ['/settings/accounts', 'settings'],
  ['/settings/connectors', 'settings'],
  ['/settings/secrets', 'settings'],
  ['/settings/agentic-access', 'apiKeys'],
]

function matches(pattern: string, path: string): boolean {
  const want = pattern.split('/')
  const got = path.split('/')
  if (want.length !== got.length) return false
  return want.every((seg, i) => (seg.startsWith(':') ? got[i] !== '' : seg === got[i]))
}

/** The chunk an address renders, or undefined for an address with no page (a redirect, a 404). */
export function routeFor(path: string): RouteChunk | undefined {
  const bare = path.split(/[?#]/)[0].replace(/(.)\/$/, '$1')
  return PATTERNS.find(([pattern]) => matches(pattern, bare))?.[1]
}

type ChunkModule<C extends RouteChunk> = Awaited<ReturnType<(typeof routeChunks)[C]>>

const inFlight = new Map<RouteChunk, Promise<unknown>>()
const loaded = new Map<RouteChunk, unknown>()

/**
 * Fetch a chunk, once. The module is kept when it lands so a page can render it
 * SYNCHRONOUSLY from then on (`loadedChunk`): React.lazy suspends on every first
 * render, even for a module already in hand, and that suspension is a loader
 * drawn over a page whose code and data are both ready.
 *
 * `reload` says whether a stale module may reload the page. Only a load a
 * navigation or the boot started may: a hover or focus prefetch that fails
 * stays quiet, so it never reloads a page the user is typing into and drops
 * their edits. The next navigation to that page retries and reloads then.
 */
export function loadChunk<C extends RouteChunk>(
  chunk: C,
  { reload = true }: { reload?: boolean } = {},
): Promise<ChunkModule<C>> {
  let p = inFlight.get(chunk)
  if (!p) {
    p = routeChunks[chunk]().then(
      (m) => {
        loaded.set(chunk, m)
        return m
      },
      (err: unknown) => {
        // A failed fetch (a deploy swapped the chunk, the network dropped) may be
        // retried by the next render or hover.
        inFlight.delete(chunk)
        throw err
      },
    )
    inFlight.set(chunk, p)
  }
  // Per caller, not on the shared promise: a navigation that joins a prefetch
  // already in flight still reloads when that fetch fails.
  const mine = reload
    ? p.catch((err: unknown) => {
        reloadOnceForStaleModule(err)
        throw err
      })
    : p
  return mine as Promise<ChunkModule<C>>
}

/** A chunk's module if it has already landed, else undefined. */
export function loadedChunk<C extends RouteChunk>(chunk: C): ChunkModule<C> | undefined {
  return loaded.get(chunk) as ChunkModule<C> | undefined
}

/**
 * Start fetching the page an address renders. Safe to call on every hover: a
 * chunk already loaded or in flight is not fetched again, and an address with
 * no page does nothing.
 */
export function prefetchRoute(path: string): void {
  const chunk = routeFor(path)
  if (chunk) loadChunk(chunk, { reload: false }).catch(() => {})
}

/** Props to spread on a link: hovering or focusing it prefetches its page. */
export function prefetchProps(path: string): { onPointerEnter: () => void; onFocus: () => void } {
  return { onPointerEnter: () => prefetchRoute(path), onFocus: () => prefetchRoute(path) }
}

/**
 * How long the boot waits on the first page's code before drawing anyway. A slow
 * network must never hold the app behind the loader: past this the page's own
 * Suspense boundary takes over.
 */
const BOOT_CHUNK_CAP_MS = 1500

let bootReady = true
const bootListeners = new Set<() => void>()

function settleBoot() {
  if (bootReady) return
  bootReady = true
  for (const l of bootListeners) l()
}

/**
 * At boot, before the first render: start fetching the page the address in the
 * bar renders, in parallel with the cache restore and the session check, and
 * hold the app's one loader until it lands (or BOOT_CHUNK_CAP_MS passes). So
 * the first paint after the loader is the whole page, drawn from the persisted
 * cache — not the chrome around a second loader while the page's code arrives.
 */
export function preloadBootRoute(path: string, capMs = BOOT_CHUNK_CAP_MS): void {
  const chunk = routeFor(path)
  if (!chunk || loadedChunk(chunk)) return
  bootReady = false
  const timer = setTimeout(settleBoot, capMs)
  loadChunk(chunk)
    .catch(() => {})
    .finally(() => {
      clearTimeout(timer)
      settleBoot()
    })
}

/** Whether the boot page's code has landed, or the wait on it has run out. */
export function isBootRouteReady(): boolean {
  return bootReady
}

/** Subscribe to the boot page becoming ready (useSyncExternalStore). */
export function subscribeBootRoute(listener: () => void): () => void {
  bootListeners.add(listener)
  return () => bootListeners.delete(listener)
}

/** How long a reload for a stale module counts as the last one, so a real outage never loops. */
const STALE_RELOAD_WINDOW_MS = 10_000
const STALE_RELOAD_KEY = 'warike-stale-module-reload'
const STALE_MODULE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i

/**
 * A page's module could not be fetched because the one the page asked for no
 * longer exists: a deploy replaced the build's chunks, or the dev server
 * re-bundled its dependencies. The page in memory is the stale part, and a
 * reload is the whole fix — it asks for the current modules. Once per window,
 * so an address that stays broken shows its error instead of reloading forever.
 */
export function reloadOnceForStaleModule(err: unknown, opts: ReloadOptions = {}): boolean {
  const message = err instanceof Error ? err.message : String(err)
  if (!STALE_MODULE.test(message)) return false
  return reloadOnce(opts)
}

interface ReloadOptions {
  storage?: Storage
  now?: number
  reload?: () => void
}

/**
 * Vite's `vite:preloadError`: a dynamic import's CSS or JS dependency could
 * not be preloaded — the same stale build, reached through any lazy import (an
 * overlay in overlays/registry.tsx, a page's stylesheet), not only a page chunk.
 * Reloads once per the same window; when it does, the event is prevented so
 * Vite does not also throw into a page that is about to go away. Returns the
 * unsubscribe.
 */
export function reloadOnPreloadError(
  target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> = window,
  opts: ReloadOptions = {},
): () => void {
  const onError = (event: Event) => {
    if (reloadOnce(opts)) event.preventDefault()
  }
  target.addEventListener('vite:preloadError', onError)
  return () => target.removeEventListener('vite:preloadError', onError)
}

/** Reload, unless one already happened within the window. */
function reloadOnce({
  storage = safeSession(),
  now = Date.now(),
  reload = () => window.location.reload(),
}: ReloadOptions): boolean {
  // No place to remember the last reload means no window, and no window means a
  // loop: without storage the page shows its error instead.
  if (!storage) return false
  try {
    const last = Number(storage.getItem(STALE_RELOAD_KEY) ?? 0)
    if (now - last < STALE_RELOAD_WINDOW_MS) return false
    storage.setItem(STALE_RELOAD_KEY, String(now))
  } catch {
    return false
  }
  reload()
  return true
}

function safeSession(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage
  } catch {
    return undefined
  }
}
